import React from 'react'
import styled from 'styled-components'
import { OptionItem } from '../common'

/** DrumPickerPropsのtype定義 */
type DrumPickerProps<T> = {
  /** 選択肢 */
  options: Array<T | OptionItem<T>>
  /** 選択中のインデックス */
  selectedIndex?: number
  /** 選択中の値 */
  selectedValue?: T
  /** コンポーネントの高さ */
  height: number
  /** コンポーネントの幅 */
  width: number
  /** アイテムの高さ(デフォルト36px) */
  itemHeight?: number
  /** 選択変更時のコールバック */
  onChanged?: (index: number) => void
  /** 選択肢に値が設定されている場合の変更時コールバック */
  onValueChanged?: (value: T) => void
}

/** ドラムピッカー */
const DrumPicker = <T,>(prop: DrumPickerProps<T>): JSX.Element => {
  const {
    options,
    selectedIndex,
    selectedValue,
    height,
    width,
    itemHeight = 36,
    onChanged,
    onValueChanged,
  } = prop
  const [isMoving, setIsMoving] = React.useState(false)
  const [startY, setStartY] = React.useState(0)
  const [scrollerTranslate, setScrollerTranslate] = React.useState(0)

  const index = React.useMemo(
    () =>
      selectedIndex ?? selectedValue
        ? options.findIndex(
            (x: T | OptionItem<T>) =>
              ('value' in x ? x.value : x) === selectedValue
          )
        : 0,
    [selectedIndex, selectedValue, options]
  )

  const minTranslate = React.useMemo(
    () => height / 2 - itemHeight * options.length + itemHeight / 2,
    [height, itemHeight, options]
  )
  const maxTranslate = React.useMemo(() => height / 2 - itemHeight / 2, [
    height,
    itemHeight,
  ])

  const onValueSelected = (index: number, value?: T): void => {
    if (onChanged) {
      onChanged(index)
    }
    if (onValueChanged && value != null) {
      onValueChanged(value)
    }
  }

  const moveYPosition = (diff: number): void => {
    let nextScrollerTranslate = scrollerTranslate + diff / 2
    if (nextScrollerTranslate < minTranslate) {
      nextScrollerTranslate =
        minTranslate - Math.pow(minTranslate - nextScrollerTranslate, 0.8)
    } else if (nextScrollerTranslate > maxTranslate) {
      nextScrollerTranslate =
        maxTranslate + Math.pow(nextScrollerTranslate - maxTranslate, 0.8)
    }
    setScrollerTranslate(nextScrollerTranslate)
  }

  const onTimeOut = (): void => {
    let activeIndex
    if (scrollerTranslate > maxTranslate) {
      activeIndex = 0
    } else if (scrollerTranslate < minTranslate) {
      activeIndex = options.length - 1
    } else {
      activeIndex = -Math.floor((scrollerTranslate - maxTranslate) / itemHeight)
    }
    const item = options[activeIndex]
    onValueSelected(activeIndex, 'value' in item ? item.value : item)
  }

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>): void => {
    setStartY(event.targetTouches[0].pageY)
  }
  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (!isMoving) {
      setIsMoving(true)
      return
    }
    moveYPosition((event.targetTouches[0].pageY - startY) * 0.5)
  }
  const handleTouchEnd = (): void => {
    if (!isMoving) {
      return
    }

    setIsMoving(false)
    setStartY(0)

    setTimeout(onTimeOut, 0)
  }

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
    setStartY(event.pageY)
    setIsMoving(true)
  }
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!isMoving) {
      return
    }
    moveYPosition((event.pageY - startY) * 0.5)
  }
  const handleMouseUp = (): void => {
    if (!isMoving) {
      return
    }

    setIsMoving(false)
    setStartY(0)

    setTimeout(onTimeOut, 0)
  }

  React.useEffect(() => {
    setScrollerTranslate(height / 2 - itemHeight / 2 - index * itemHeight)
  }, [height, index, itemHeight])

  return (
    <PickerContainer height={height} width={width}>
      <PickerInner>
        <PickerColumn>
          <PickerScroller
            translateY={scrollerTranslate}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {options.map((option, optionIndex) => (
              <PickerItem
                key={`key_${optionIndex}`}
                height={itemHeight}
                isSelected={optionIndex === index}
              >
                {'label' in option ? option.label : option}
              </PickerItem>
            ))}
          </PickerScroller>
        </PickerColumn>
        <PickerHighlight height={itemHeight} />
      </PickerInner>
    </PickerContainer>
  )
}

export default React.memo(DrumPicker) as typeof DrumPicker

/** ルートのスタイル定義 */
const PickerContainer = styled.div<{ height: number; width: number }>`
  width: ${(props): string => `${props.width}vw`};
  height: ${(props): string => `${props.height}px`};
  cursor: pointer;
  user-select: none;

  &&,
  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }
`

/** ピッカー内部のスタイル定義 */
const PickerInner = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  height: 100%;
  padding: 0 20px;
  font-size: 1.2em;
`

/** ドラムロールの行のスタイル定義 */
const PickerColumn = styled.div`
  position: relative;
  flex: 1 1;
  max-height: 100%;
  overflow: hidden;
  text-align: center;
`

/** スクロールしたときのスタイル定義 */
const PickerScroller = styled.div<{ translateY: number }>`
  display: flex;
  flex-direction: column;
  transform: ${(props): string => `translate3d(0, ${props.translateY}px, 0)`};
  transition: 1000ms;
  transition-timing-function: ease-out;
`

/** １つ分のデータのスタイル定義 */
const PickerItem = styled.div<{ height: number; isSelected: boolean }>`
  position: relative;
  height: ${(props): string => `${props.height}px`};
  padding: 0 10px;
  overflow: hidden;
  color: ${(props): string => (props.isSelected ? '#222' : '#999999')};
  line-height: ${(props): string => `${props.height}px`};
  white-space: nowrap;
  text-overflow: ellipsis;
`

/** ピッカーの高さ */
const PickerHighlight = styled.div<{ height: number }>`
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: ${(props): string => `${props.height}px`};
  margin-top: ${(props): string => `-${props.height / 2}px`};
  pointer-events: none;

  &&:before,
  &&:after {
    position: absolute;
    right: auto;
    left: 0;
    display: block;
    width: 100%;
    height: 1px;
    background-color: #d9d9d9;
    transform: scaleY(0.5);
    content: ' ';
  }

  &&:before {
    top: 0;
    bottom: auto;
  }

  &&:after {
    top: auto;
    bottom: 0;
  }
`
